import { sortBy } from 'lodash'
import _get from 'lodash/get'
import moment from 'moment'

export default {
	removeNonNumericCharsOMD(phone) {
		return phone.replace(/\D/g, '')
	},

	// Method to format the phone number as xxx-xxx-xxxx
	newFormatPhoneNumberOMD(phone) {
		const cleaned = ('' + phone).replace(/\D/g, '') // Remove non-numeric characters
		const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/)

		if (match) {
			return `${match[2]}-${match[3]}-${match[4]}`
		}

		return phone // If it doesn't match the format, return the original
	},

	validateCallBackNumberDashed(number) {
		return !(
			!number ||
			number.length < 12 ||
			number[0] === '0' ||
			number[0] === '1'
		)
	},

	formatPhoneNumberAsStandard(number) {
		// Remove non-numeric characters
		let formatted = number.replace(/\D/g, '')

		// Add the phone number format (xxx-xxx-xxxx)
		if (formatted.length <= 3) {
			number = formatted // First part
		} else if (formatted.length <= 6) {
			number = `${formatted.slice(0, 3)}-${formatted.slice(3)}` // Second part
		} else {
			number = `${formatted.slice(0, 3)}-${formatted.slice(
				3,
				6
			)}-${formatted.slice(6, 10)}` // Final format
		}
		return number
	},

	getDraftValue(key, sid) {
		if (!key || !sid) return ''
		const drafts = JSON.parse(localStorage.getItem('saved_drafts') || '{}')
		return drafts[`${key}_${sid}`] || ''
	},

	setDraftValue(key, value, sid) {
		if (!key || !sid) return
		const drafts = JSON.parse(localStorage.getItem('saved_drafts') || '{}')
		drafts[`${key}_${sid}`] = value
		localStorage.setItem('saved_drafts', JSON.stringify(drafts))
	},

	resetDraftValues() {
		localStorage.removeItem('saved_drafts')
	},

	containNewAlphabets(msg, tmplt, minCount, maxCount) {
		const message = (msg || '').trim().replace(/\s+/g, ' ')
		const template = (tmplt || '').trim() + '\n'

		const countTotalWhitespaces = str => {
			const matches = str.match(/(\r\n)|\r|\n| /g)
			return matches ? matches.length : 0
		}

		const getWords = text =>
			text
				.replace(/[\r\n\t]+/g, ' ')
				.trim()
				.split(/\s+/)

		const messageWords = getWords((message || '').toLowerCase())
		const templateWords = getWords((template || '').toLowerCase())
		let remainingMsgWords = []
		let templateWordsMissingCount = 0
		const templateWordsCopy = [...templateWords]

		for (let i of messageWords) {
			const index = templateWordsCopy.indexOf(i)
			if (index === -1) {
				remainingMsgWords.push(i)
			} else {
				templateWordsCopy.splice(index, 1)
			}
		}

		for (let i of templateWords) {
			const index = messageWords.indexOf(i)
			if (index === -1) {
				templateWordsMissingCount++
			}
		}

		const remainingMsgChars = remainingMsgWords.join('')
		const totalWhitespacesMsg = countTotalWhitespaces(message)
		const totalWhitespacesTemplate = countTotalWhitespaces(template)

		const resultLength =
			remainingMsgChars.length +
			totalWhitespacesMsg -
			(totalWhitespacesTemplate -
				templateWordsMissingCount -
				(templateWordsMissingCount < templateWords.length ? 1 : 0))

		return resultLength >= minCount && resultLength <= maxCount
	},

	normalizeString(str) {
		return str
			.toLowerCase()
			.replace(/\r\n/g, '\n') // normalize CRLF to LF
			.replace(/\s+/g, ' ') // replace multiple whitespace (spaces, tabs, newlines) with single space
			.trim()
	},

	getSpecificCallAttributes(call, attribute, defaultValue = null) {
		const callAttributes = _get(call, 'call_attributes', [])
		const found = callAttributes?.find(
			attr => attr.internal_name === attribute
		)
		const value =
			typeof found?.value === 'string' ? found.value.trim() : found?.value
		return value ? value : defaultValue
	},

	isWMACall(call) {
		const events = call?.events || []
		const hasTransferEvent = events.some(
			event =>
				event.type === 'smart_transfer' ||
				event.type === 'external_transfer'
		)
		const isWmaEnabled = !!call?.wma_allow_reply
		const isCallCompleted = !!call?.completed_at
		return isWmaEnabled && !(isCallCompleted && hasTransferEvent)
	},

	capitalizeFirstLetter(string) {
		if (!string || typeof string !== 'string') return ''
		return string.charAt(0).toUpperCase() + string.slice(1)
	},

	sortPartners(partners = []) {
		return sortBy(partners, p =>
			p.name
				?.toLowerCase()
				.replace(/[()]/g, '') // remove brackets
				.replace(/[\s-]/g, '') // remove spaces & hyphens
				.trim()
		)
	},

	async copyToClipboard(data) {
		try {
			await navigator.clipboard.writeText(data)
			return true
		} catch (err) {
			return false
		}
	},

	generateSummary(call) {
		try {
			if (!call || Object.keys(call).length === 0) {
				return ''
			}

			const lines = []
			const labelWidth = 20
			const maxLineLength = 80 // Max characters per line
			let s = '-'
			let dashLineFeed = s.repeat(maxLineLength)

			// Helper function to format label-value pairs with alignment
			const formatLine = (label, value) => {
				return `${label.padEnd(labelWidth)}${value}`
			}

			// Helper function to wrap text by whole words
			const wrapTextByWords = (text, maxLen) => {
				const words = text.split(/\s+/)
				const lines = []
				let currentLine = ''

				words.forEach(word => {
					if (!word) return
					if (currentLine.length === 0) {
						currentLine = word
					} else if (currentLine.length + 1 + word.length <= maxLen) {
						currentLine += ' ' + word
					} else {
						lines.push(currentLine)
						currentLine = word
					}
				})

				if (currentLine) {
					lines.push(currentLine)
				}

				return lines
			}

			// Helper function to format long text with wrapping (max 80 char per line)
			const formatLongText = (label, value) => {
				if (!value) {
					return `${label.padEnd(labelWidth)}${value}`
				}

				const indent = ' '.repeat(labelWidth)
				const valueMaxLen = maxLineLength - labelWidth

				// Check if value has newlines or is long
				if (!value.includes('\n') && value.length <= valueMaxLen) {
					return `${label.padEnd(labelWidth)}${value}`
				}

				const result = []
				const paragraphs = value.split('\n')

				paragraphs.forEach((paragraph, index) => {
					const wrappedLines = wrapTextByWords(paragraph, valueMaxLen)

					if (index === 0) {
						// First paragraph starts with label
						wrappedLines.forEach((line, lineIndex) => {
							if (lineIndex === 0) {
								result.push(
									`${label.padEnd(labelWidth)}${line}`
								)
							} else {
								result.push(`${indent}${line}`)
							}
						})
						if (wrappedLines.length === 0) {
							result.push(`${label.padEnd(labelWidth)}`)
						}
					} else {
						// Subsequent paragraphs start with indent
						wrappedLines.forEach(line => {
							result.push(`${indent}${line}`)
						})
						if (wrappedLines.length === 0) {
							result.push(`${indent}`)
						}
					}
				})

				return result.join('\n')
			}

			// Header
			lines.push(
				'Clarus Care, Message Details, Exported: ' +
					moment().format('MM/DD/YYYY hh:mm A')
			)
			lines.push('')

			// Date/Time of Call
			if (call.created_at) {
				const timestamp = moment
					.utc(call.created_at)
					.local()
					.format('MM/DD/YYYY hh:mm A')
				lines.push(formatLine('Received:', timestamp))
			}

			// Patient Name
			lines.push(
				formatLine('Patient Name:', call.patient_name || 'Unavailable')
			)

			// Provider
			const providerName =
				call.patient_provider?.full_name || 'Unavailable'
			lines.push(formatLine('Provider:', providerName))

			// Phone Number
			let phoneNumber = 'Unavailable'
			if (call.callback_number) {
				try {
					phoneNumber = this.newFormatPhoneNumberOMD(
						call.callback_number
					)
				} catch (e) {
					phoneNumber = call.callback_number
				}
			}
			lines.push(formatLine('Phone Number:', phoneNumber))

			// Patient DOB
			lines.push(
				formatLine('Patient DOB:', call.patient_dob || 'Unavailable')
			)

			// Patient Message
			lines.push(
				formatLongText(
					'Patient Message:',
					call.patient_message || 'Unavailable'
				)
			)

			lines.push('')

			// Notes – table without border: Date 12 (10+2), Created By 22 (20+2), Note 46
			const dateColWidth = 12
			const createdByColWidth = 22
			const noteColWidth = 46
			const padDate = str =>
				str.slice(0, dateColWidth).padEnd(dateColWidth)
			const padCreatedBy = str =>
				str.slice(0, createdByColWidth).padEnd(createdByColWidth)
			const padNote = str =>
				str.slice(0, noteColWidth).padEnd(noteColWidth)
			const emptyDate = ' '.repeat(dateColWidth)
			const emptyCreatedBy = ' '.repeat(createdByColWidth)
			const emptyNote = ' '.repeat(noteColWidth)
			const colSep = '- '
			const e = ' '.repeat(2)

			lines.push('Notes:')
			lines.push('')
			lines.push(
				`${'Date'.padEnd(dateColWidth)}${e}${'Created By'.padEnd(
					createdByColWidth
				)}${e}Note`
			)
			lines.push(dashLineFeed)

			const notes = [...call.notes].sort((a, b) => b.id - a.id)
			notes.forEach(note => {
				const dateStr = moment
					.utc(note.created_at)
					.local()
					.format('MM/DD/YYYY')
				const timeStr = moment
					.utc(note.created_at)
					.local()
					.format('hh:mm A')
				const dateLines = [padDate(dateStr), padDate(timeStr)]

				const userName = note.user
					? `${note.user.first_name || ''} ${note.user.last_name ||
							''}`.trim() || 'Unknown'
					: 'Unknown'
				const createdByLines = wrapTextByWords(userName, 20).map(line =>
					padCreatedBy(line)
				)

				const noteContent =
					note?.type === 'completion'
						? `Completion note: ${note.content}`
						: note.content || ''
				const noteLines = []
				noteContent.split('\n').forEach((paragraph, idx) => {
					if (idx > 0) noteLines.push(emptyNote)
					const wrapped = wrapTextByWords(paragraph, noteColWidth)
						.flatMap(line => {
							const chunks = []
							for (
								let i = 0;
								i < line.length;
								i += noteColWidth
							) {
								chunks.push(line.slice(i, i + noteColWidth))
							}
							return chunks
						})
						.map(line => padNote(line))
					wrapped.forEach(line => noteLines.push(line))
				})

				const rowCount = Math.max(
					dateLines.length,
					createdByLines.length,
					noteLines.length,
					1
				)
				for (let i = 0; i < rowCount; i++) {
					const dateCell = dateLines[i] ?? emptyDate
					const createdByCell = createdByLines[i] ?? emptyCreatedBy
					const noteCell = noteLines[i] ?? emptyNote
					// First row: " - "; other rows: 3 spaces so Created By column aligns, note column unchanged
					const sep = i === 0 ? colSep : ' '.repeat(colSep.length)
					lines.push(
						`${dateCell}${sep}${createdByCell}${sep}${noteCell}`
					)
				}
				lines.push('')
			})
			lines.push('')

			// Activity History – table: Date 12, " - ", Activity 68 (date - activity, then time)
			if (call.events && call.events.length > 0) {
				const activityColWidth = maxLineLength - dateColWidth - 3 // 3 for " - "
				const emptyActivity = ' '.repeat(activityColWidth)
				const activityColSep = '- '
				const e = ' '.repeat(2)

				lines.push('Activity History:')
				lines.push('')
				lines.push(
					`${'Date'.padEnd(dateColWidth)}${e}${'Activity'.padEnd(
						activityColWidth
					)}`
				)
				lines.push(dashLineFeed)

				const events = [...call.events].sort((a, b) => b.id - a.id)
				events.forEach(event => {
					const dateStr = moment
						.utc(event.created_at)
						.local()
						.format('MM/DD/YYYY')
					const timeStr = moment
						.utc(event.created_at)
						.local()
						.format('hh:mm A')
					const eventDateLines = [padDate(dateStr), padDate(timeStr)]

					const eventSummary = event.summary || ''
					const activityLines = eventSummary
						.split('\n')
						.flatMap(p => wrapTextByWords(p, activityColWidth))
						.map(line => line.padEnd(activityColWidth))

					const eventRowCount = Math.max(
						eventDateLines.length,
						activityLines.length,
						1
					)
					for (let i = 0; i < eventRowCount; i++) {
						const dateCell = eventDateLines[i] ?? emptyDate
						const activityCell = activityLines[i] ?? emptyActivity
						const sep = i === 0 ? activityColSep : ''
						lines.push(`${dateCell}${sep}${activityCell}`)
					}
					lines.push('')
				})
			}

			const result = lines.join('\n')
			return result
		} catch (error) {
			console.error('Error generating summary:', error)
			return ''
		}
	},
}
